import { Link } from '@tanstack/react-router';
import { ButtonGroup } from '@progress/kendo-react-buttons';
import { HeroIcon } from '../shared/HeroIcon.tsx';
import { usePublicCompany } from './hooks/usePublicCompany.ts';
import { useUserInfo } from './hooks/useUserInfo.ts';
import { useKeycloak } from '../shared/auth/KeycloakProvider.tsx';
import { useEffect } from 'react';
import { ModulePrivileges } from '../gql/graphql.ts';
import { ConditionalWrapper } from '../shared/ConditionalWrapper.tsx';
import { getBackendUrl, restApiUrl } from '../shared/helpers/backendUrl';

interface IAdminLayoutProps {
  children?: React.ReactNode;
}

export const PublicLayout: React.FC<IAdminLayoutProps> = ({ children }) => {
  const [publicCompany] = usePublicCompany();
  const { userInfo } = useUserInfo();

  const embedMode = window.__BTS_EMBED_MODE__ === true;
  const footerMenuSites = publicCompany?.sites.filter(
    (site) => site.isFooterSite,
  );

  useEffect(() => {
    // These CSS variables need to be set on the body, because Kendo will render portals/modals outside of the #root div
    // and we want the styles to apply there as well.
    // Tailwind Grey 200
    document.body.style.setProperty('--kendo-color-border-alt', '#e5e7eb');
    if (publicCompany?.visualSettings.primaryColor) {
      document.body.style.setProperty(
        '--theme-primary-color',
        publicCompany?.visualSettings.primaryColor || '#ff7f51',
      );
      document.body.style.setProperty(
        '--theme-primary-lighter-color',
        'color-mix(in srgb,var(--theme-primary-color),#fff 15%)',
      );
      document.body.style.setProperty(
        '--theme-primary-darker-color',
        'color-mix(in srgb,var(--theme-primary-color),#000 15%)',
      );
      // Kendo color overrides
      document.body.style.setProperty(
        '--kendo-color-primary-active',
        'var(--theme-primary-darker-color)',
      );
      document.body.style.setProperty(
        '--kendo-color-primary-emphasis',
        'var(--theme-primary-lighter-color)',
      );
      document.body.style.setProperty(
        '--kendo-color-primary-hover',
        'color-mix(in srgb,var(--theme-primary-color),#000 30%)',
      );
      document.body.style.setProperty(
        '--kendo-color-primary',
        publicCompany?.visualSettings.primaryColor || '#ff7f51',
      );
      document.body.style.setProperty(
        '--kendo-color-primary-subtle-hover',
        'color-mix(in srgb,var(--theme-primary-color),#fff 30%)',
      );
      document.body.style.setProperty(
        '--kendo-color-primary-subtle',
        'color-mix(in srgb,var(--theme-primary-color),#fff 45%)',
      );
    }
    if (publicCompany?.visualSettings.primaryTextColor) {
      document.body.style.setProperty(
        '--theme-text-color',
        publicCompany?.visualSettings.primaryTextColor || 'rgb(23,23,23)',
      );
      document.body.style.setProperty(
        '--theme-text-lighter-color',
        'color-mix(in srgb,var(--theme-text-color),#fff 60%)',
      );
    }
  }, [publicCompany]);

  useEffect(() => {
    const css = publicCompany?.visualSettings.customCss;
    if (!css) return;
    const el = document.createElement('style');
    el.id = 'bts-tenant-css';
    el.textContent = css;
    document.head.appendChild(el);
    return () => {
      el.remove();
    };
  }, [publicCompany?.visualSettings.customCss]);

  return (
    <div
      className={`grid h-full grid-cols-[1fr_min(1280px,calc(100%-32px))_1fr] md:grid-cols-[1fr_min(1280px,calc(100%-64px))_1fr] ${embedMode ? 'grid-rows-[min-content_auto]' : 'grid-rows-[70px_min-content_auto_minmax(auto,120px)]'}`}
    >
      <title>{`${publicCompany?.companyName || 'Seminarhaus'} - ${import.meta.env.VITE_APP_TITLE}`}</title>
      {embedMode && (
        <div className="col-start-2 col-end-3 flex justify-end pt-4">
          <PublicNavButtons />
        </div>
      )}
      {!embedMode && (
        <div
          className={`z-10 col-span-3 grid grid-cols-subgrid border-b bg-white shadow-md ${embedMode ? 'px-4 pb-4' : 'p-4'}`}
        >
          <div className="col-start-2 col-end-3 flex items-center justify-between">
            {publicCompany?.companyName && (
              <Link to="/" className="h-full w-auto">
                <h3 className="text-2xl">{publicCompany?.companyName}</h3>
              </Link>
            )}
            <div className="flex items-center gap-4">
              {userInfo?.data && (
                <span className="hidden md:block">
                  Hallo, {userInfo?.data?.currentUser?.address.firstName}
                </span>
              )}
              <PublicNavButtons />
            </div>
          </div>
        </div>
      )}
      {!embedMode && publicCompany?.visualSettings.pathToBanner && (
        <div className="col-start-1 col-end-4 lg:col-start-2 lg:col-end-3">
          <img
            src={restApiUrl(publicCompany.visualSettings.pathToBanner)}
            alt={'Bannergrafik von ' + publicCompany.companyName}
            className="w-full"
          />
        </div>
      )}
      <div className="col-start-2 col-end-3 grid h-full grid-cols-1 content-start gap-6 py-6 md:grid-cols-12 md:py-8">
        {children}
      </div>
      {!embedMode && (
        <footer className="col-span-3 row-start-4 grid grid-cols-subgrid">
          <div className="col-start-2 flex h-full w-full flex-col items-center gap-4 border-t border-zinc-300 py-8">
            <div className="font-familjen text-black">
              Seminaranmeldung bereitgestellt durch BTS Web
            </div>
            {Boolean(footerMenuSites?.length) && (
              <nav>
                <ul className="flex h-full items-center justify-center gap-4 text-sm">
                  {footerMenuSites?.map(({ title, slug, externalUrl }) => (
                    <li key={slug}>
                      <a
                        href={externalUrl || slug}
                        className="hover:text-black"
                        {...(externalUrl
                          ? {
                              target: '_blank',
                            }
                          : {})}
                      >
                        {title}
                      </a>
                    </li>
                  ))}
                </ul>
              </nav>
            )}
          </div>
        </footer>
      )}
    </div>
  );
};

const PublicNavButtons: React.FC = () => {
  const [publicCompany] = usePublicCompany();
  const { userInfo } = useUserInfo();
  const { login, logout } = useKeycloak();
  const embedMode = window.__BTS_EMBED_MODE__ === true;

  // ButtonGroup applies positional className to each child via cloneElement,
  // so children must be flat (no Fragment wrappers) — passing a Fragment with
  // className triggers a React 19 warning.
  return (
    <ButtonGroup>
      {userInfo?.data
        ? [
            <Link
              key="profile"
              to={`/profile/myseminars`}
              className="k-button k-button-md k-button-outline k-button-outline-secondary k-rounded-md"
            >
              <HeroIcon
                name={'AcademicCap'}
                className="inline-block h-5 w-5 shrink-0"
              />
              <span className="hidden md:block">Profil</span>
            </Link>,
            userInfo.data.currentUser?.privileges?.includes(
              ModulePrivileges.Company,
            ) ? (
              <ConditionalWrapper
                key="admin"
                condition={embedMode}
                wrapper={(c) => (
                  <a
                    href={`${getBackendUrl()}/admin/`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="k-button k-button-md k-button-outline k-button-outline-secondary k-rounded-md"
                  >
                    {c}
                  </a>
                )}
                falseWrapper={(c) => (
                  <Link
                    to="/admin"
                    className="k-button k-button-md k-button-outline k-button-outline-secondary k-rounded-md"
                  >
                    {c}
                  </Link>
                )}
              >
                <HeroIcon
                  name={'Briefcase'}
                  className="inline-block h-5 w-5 shrink-0"
                />
                <span className="hidden md:block">Admin-Panel</span>
              </ConditionalWrapper>
            ) : null,
            <button
              key="logout"
              onClick={() =>
                void logout().catch((err) =>
                  console.error('[Auth] Logout failed:', err),
                )
              }
              className="k-button k-button-md k-button-outline k-button-outline-secondary k-rounded-md"
            >
              <HeroIcon
                name={'ArrowLeftStartOnRectangle'}
                className="inline-block h-5 w-5 shrink-0"
              />
              <span className="hidden md:block">Abmelden</span>
            </button>,
          ]
        : !publicCompany?.functionalSettings?.disableUserAccounts
          ? [
              <Link
                key="register"
                to="/register"
                className="k-button k-button-md k-button-outline k-button-outline-secondary k-rounded-md"
              >
                <HeroIcon
                  name={'ArrowLeftStartOnRectangle'}
                  className="inline-block h-5 w-5 shrink-0"
                />
                Registrieren
              </Link>,
              <button
                key="login"
                onClick={() =>
                  void login().catch((err) =>
                    console.error('[Auth] Login failed:', err),
                  )
                }
                className="k-button k-button-md k-button-outline k-button-outline-secondary k-rounded-md"
              >
                <HeroIcon
                  name={'Key'}
                  className="inline-block h-5 w-5 shrink-0"
                />
                Login
              </button>,
            ]
          : null}
    </ButtonGroup>
  );
};
